import { useMemo } from "react";

export const normaliseToolKey = (value) =>
  String(value || "").toLowerCase().replace(/[^a-z0-9]/g, "");

// Slack/MS Teams tasks record their destination on `output` rather than
// `integrations`, so they're folded into the same tool-name list here.
const CHANNEL_OUTPUT_KEYS = new Set(["slack", "microsoftteams"]);

export const getTaskToolNames = (task) => {
  const integrations = Array.isArray(task?.integrations) ? task.integrations : [];
  return CHANNEL_OUTPUT_KEYS.has(normaliseToolKey(task?.output))
    ? [...integrations, task.output]
    : integrations;
};

const formatOutputLabel = (value) => {
  if (!value) return "Unknown";
  return value
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

/**
 * Base tool catalog (value/label only, no connected/count annotations) for a
 * location — every tool mentioned on at least one task plus every tool in
 * the live integrations catalog, deduped by a normalized key. Shared by the
 * Task Library's Tools filter tab (ConversationWrapper.jsx, which further
 * annotates `connected`/`count`) and the onboarding short-link resolver
 * (which only needs to match a slug against a tool name).
 */
export function useToolCatalog(tasks, integrationsCatalog) {
  return useMemo(() => {
    const seen = new Map();
    const addEntry = (rawValue) => {
      const key = normaliseToolKey(rawValue);
      if (!key || key === "chatui" || seen.has(key)) return;
      seen.set(key, { value: rawValue, label: formatOutputLabel(rawValue) });
    };
    (tasks || []).forEach((item) => getTaskToolNames(item).forEach(addEntry));
    (integrationsCatalog || []).forEach((item) =>
      addEntry(item?.provider_name || item?.provider || item?.name),
    );
    return Array.from(seen.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [tasks, integrationsCatalog]);
}
